import { handleActions } from '@housinganywhere/safe-redux';

import { ActionTypes as ProfileEditActionTypes } from 'ha/pages/ProfileEdit/constants';
import {
  AuthModalActionTypes,
  AuthModalActions,
} from 'ha/modules/AuthModal/actions';
import { ImpersonationInfo } from 'ha/services/apiV2/types/UserInfo';
import { UserApiV2 } from 'ha/services/apiV2/types/User';

import {
  AuthLogicTypes,
  FetchUserBillingInfoTypes,
  RecoverPasswordTypes,
} from './constants';
import { AuthLogicActions } from './actions';
import { Actions as FetchUserBillingInfoActions } from './actions/fetchUserBillingInfo';
import { Actions as RecoverPasswordActions } from './actions/sendRecoverPasswordRequest';
import { UserInfoResponse } from './types';

export interface AuthLogicState {
  isLoading: boolean;
  isLoaded: boolean;
  user: UserApiV2 | null;
  userInformation: UserInfoResponse | null;
  language: string;
  isAuthenticated: boolean;
  impersonatingAdmin: ImpersonationInfo | null;
  isUserBillingInfoLoading: boolean;
}

export const initialState = {
  isLoading: false,
  isLoaded: false,
  user: null,
  userInformation: null,
  language: 'en',
  isAuthenticated: false,
  impersonatingAdmin: null,
  isUserBillingInfoLoading: false,
};

const initUser = (
  state: AuthLogicState,
  payload: {
    user: UserApiV2;
    userInformation: UserInfoResponse;
  },
) => {
  const { user, userInformation } = payload;

  return {
    ...state,
    user: {
      ...state.user,
      ...user,
    },
    userInformation: {
      ...state.userInformation,
      ...userInformation,
    },
    isAuthenticated: true,
    isLoading: false,
    isLoaded: true,
  };
};

type ActionTypes =
  | AuthLogicTypes
  | AuthModalActionTypes
  | FetchUserBillingInfoTypes
  | ProfileEditActionTypes.updateUser
  | RecoverPasswordTypes;

type Actions =
  | AuthLogicActions
  | AuthModalActions
  | FetchUserBillingInfoActions
  | RecoverPasswordActions;

const reducer = handleActions<AuthLogicState, ActionTypes, Actions>(
  {
    [AuthLogicTypes.authStart]: s => ({
      ...s,
      isLoading: true,
    }),
    [AuthLogicTypes.authFail]: s => ({
      ...s,
      isLoading: false,
      isLoaded: true,
    }),
    [AuthLogicTypes.authDone]: s => ({
      ...s,
      isLoading: false,
      isLoaded: true,
    }),
    [AuthLogicTypes.fulfillImpersonation]: (s, action) => ({
      ...s,
      impersonatingAdmin: action.payload,
    }),
    [AuthLogicTypes.initUser]: (s, action) => initUser(s, action.payload),
    [AuthLogicTypes.login]: (s, action) => initUser(s, action.payload),
    // action is used only for analytics at the moment
    [AuthLogicTypes.logoutStart]: s => s,
    [AuthLogicTypes.logoutDone]: s => ({
      ...s,
      isAuthenticated: false,
      isLoaded: false,
      user: null,
      userInformation: null,
      impersonatingAdmin: null,
    }),
    [AuthLogicTypes.updateUser]: (s, action) => ({
      ...s,
      user: {
        ...s.user,
        ...action.payload,
      },
    }),
    [AuthModalActionTypes.changeModalType]: () => initialState,
    [FetchUserBillingInfoTypes.start]: s => ({
      ...s,
      isUserBillingInfoLoading: true,
    }),
    [FetchUserBillingInfoTypes.fail]: s => ({
      ...s,
      isUserBillingInfoLoading: false,
    }),
    [FetchUserBillingInfoTypes.done]: (s, action) => ({
      ...s,
      userInformation: {
        ...s.userInformation,
        ...action.payload,
      },
      isUserBillingInfoLoading: false,
    }),
    [ProfileEditActionTypes.updateUser]: (
      s,
      {
        payload: { firstName, lastName },
      }: {
        type: string;
        payload: {
          firstName: string;
          lastName: string;
        };
      },
    ) => ({
      ...s,
      user: s.user && {
        ...s.user,
        firstName,
        lastName,
      },
    }),
    [RecoverPasswordTypes.start]: s => ({
      ...s,
      isLoading: true,
    }),
    [RecoverPasswordTypes.fail]: s => ({
      ...s,
      isLoading: false,
    }),
    [RecoverPasswordTypes.done]: s => ({
      ...s,
      isLoading: false,
    }),
  },
  initialState,
);

export default reducer;
